/**
 * @fileoverview Defines the main header section of the application.
 *
 * This component renders the top navigation bar, which includes the application title,
 * main navigation controls (like sharing, sound, and theme), and user profile access.
 * It is responsive and adapts its layout for mobile and desktop screens.
 */

import {
  GlobeIcon,
  Maximize2Icon,
  Minimize2Icon,
  MoonIcon,
  SunIcon,
  UserIcon,
  Volume2Icon,
  VolumeXIcon,
  EyeIcon,
  EyeOffIcon,
  SettingsIcon,
  Menu as MenuIcon,
  Share2Icon,
  ExternalLinkIcon
} from "lucide-react";
import { useState, useEffect } from "react";
import { useTheme } from "../../../../lib/ThemeContext";
import { useSettings } from "../../../../lib/SettingsContext";
import { useLanguage, languages } from "../../../../lib/LanguageContext";
import { useTranslation } from "react-i18next";
import { useSpinner } from "../../../../lib/SpinnerContext";
import { useAuth } from "../../../../context/AuthContext";
import { LanguageSelectorModal } from '../../../../components/LanguageSelectorModal';
import LoginModal from '../../../../components/auth/LoginModal';
import ProfileModal from '../../../../components/auth/ProfileModal';
import { SavedSpinnersModal } from '../../../../components/modals/SavedSpinnersModal';
import { useBurgerMenuGuidance } from '../../../../hooks/useBurgerMenuGuidance';
import styles from '../../../../components/BurgerMenuGuidance.module.css';

/**
 * Props for the HeaderSection component.
 */
interface HeaderSectionProps {
  /** Callback function to open the mobile sidebar overlay. */
  onMobileMenuClick?: () => void;
  /** State to control the visibility of the language selector modal. */
  showLanguageModal: boolean;
  /** Function to update the visibility of the language selector modal. */
  setShowLanguageModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** Flag indicating if the current view is for a mobile device. */
  isMobile: boolean;
  /** Callback function to open the share modal. */
  onShareClick: () => void;
  /** Callback function to handle sharing a saved spinner. */
  onSavedSpinnerShare: (spinnerId: string) => void;
  /** Callback function to handle login requests from saved spinners modal. */
  onLoginRequest?: () => void;
  /** Callback function to open a saved spinner. */
  onSavedSpinnerOpen: (spinnerId: string) => void;
}

/**
 * The main header component for the application.
 * It provides top-level navigation, settings toggles, and user authentication access.
 * It adapts its layout to be a single bar on desktop and a more compact version
 * with a hamburger menu and a settings dropdown on mobile.
 * @param {HeaderSectionProps} props - The props for the component.
 * @returns {JSX.Element} The rendered header component.
 */
export const HeaderSection = ({ onMobileMenuClick, showLanguageModal, setShowLanguageModal, isMobile, onShareClick, onSavedSpinnerShare, onLoginRequest, onSavedSpinnerOpen }: HeaderSectionProps): JSX.Element => {
  const { isDarkMode } = useTheme();
  const { audioEnabled, setAudioEnabled, showAdultSpinners, toggleAdultSpinners } = useSettings();
  const { setLanguage, forceUpdate } = useLanguage();
  const { t, i18n } = useTranslation();
  const { spinnerStyles } = useSpinner();
  const { user } = useAuth();
  const { isSidebarLocked } = useSpinner();
  const { showGuidance, dismissGuidance } = useBurgerMenuGuidance();

  // Mobile responsiveness state
  const [showMobileNavOptions, setShowMobileNavOptions] = useState(false);

  const COLLAPSED_SIDEBAR_WIDTH = 80;
  const HEADER_LEFT_MARGIN = COLLAPSED_SIDEBAR_WIDTH + 8;

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showSavedSpinners, setShowSavedSpinners] = useState(false);

  // Track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handleFullscreenChange = () => {
      const docAny = document as any;
      const activeEl = document.fullscreenElement || docAny.webkitFullscreenElement || docAny.msFullscreenElement;
      setIsFullscreen(!!activeEl);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
    document.addEventListener('msfullscreenchange', handleFullscreenChange as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange as EventListener);
    };
  }, []);

  /** Toggles the browser's fullscreen mode. */
  const toggleFullScreen = () => {
    const docAny = document as any;
    const elAny = document.documentElement as any;
    const isFs = document.fullscreenElement || docAny.webkitFullscreenElement || docAny.msFullscreenElement;
    if (!isFs) {
      if (elAny.requestFullscreen) {
        elAny.requestFullscreen();
      } else if (elAny.webkitRequestFullscreen) {
        elAny.webkitRequestFullscreen();
      } else if (elAny.msRequestFullscreen) {
        elAny.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (docAny.webkitExitFullscreen) {
        docAny.webkitExitFullscreen();
      } else if (docAny.msExitFullscreen) {
        docAny.msExitFullscreen();
      }
    }
  };

  /** Toggles the visibility of the mobile navigation dropdown menu. */
  const toggleMobileNav = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setShowMobileNavOptions(prev => !prev);
  };

  /** Toggles the application's audio on or off. */
  const toggleAudio = () => {
    setAudioEnabled(!audioEnabled);
  };

  /** Toggles the application's theme between light and dark mode. */
  const toggleTheme = () => {
    document.documentElement.classList.toggle('dark');
  };

  /** Returns the full name of the currently selected language. */
  const getCurrentLanguageName = () => {
    // Use the actual i18n language to ensure consistency
    const actualLanguage = i18n.language;
    const foundLanguage = languages.find(lang => lang.code === actualLanguage);
    const languageName = foundLanguage?.name || 'English';
    

    
    return languageName;
  };

  const handleLoginRequest = () => {
    setShowLoginModal(true);
  };

  /**
   * Handles clicks on the user icon.
   * Opens the profile modal for logged-in users, or the login modal for guests.
   * @param {boolean} isMobile - Indicates if the click originated from the mobile dropdown.
   */
  const handleUserClick = (isMobile: boolean) => {
    if (user) {
      setShowProfileModal(true);
    } else {
      setShowLoginModal(true);
    }
    if (isMobile) setShowMobileNavOptions(false);
  };

  // Close login modal when user is authenticated
  useEffect(() => {
    if (user && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [user, showLoginModal]);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate();
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, forceUpdate]);

  /**
   * Renders the set of navigation icons.
   * Adapts the layout for either the horizontal desktop bar or the vertical mobile dropdown.
   * @param {boolean} isMobileDropdown - If true, renders the icons in a list for the mobile menu.
   * @returns {JSX.Element} A fragment containing the navigation icon buttons.
   */
  const renderNavIcons = (isMobileDropdown: boolean) => (
    <div className={`flex ${isMobileDropdown ? 'flex-col items-start gap-1' : 'items-center gap-2'}`}>
      {/* Share button */}
      <button 
        onClick={() => {
          onShareClick();
          if (isMobileDropdown) setShowMobileNavOptions(false);
        }}
        className={`flex rounded-full text-white hover:bg-gray-700 transition ${isMobileDropdown ? 'w-full justify-start px-3 py-2 text-left' : 'items-center justify-center w-8 h-8'}`}
        title={t('header.share')}
      >
        <Share2Icon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
        {isMobileDropdown && t('header.share')}
      </button>

      {/* Open button */}
      <button 
        onClick={() => {
          setShowSavedSpinners(true);
          if (isMobileDropdown) setShowMobileNavOptions(false);
        }}
        className={`flex rounded-full text-white hover:bg-gray-700 transition ${isMobileDropdown ? 'w-full justify-start px-3 py-2 text-left' : 'items-center justify-center w-8 h-8'}`}
        title={t('header.open')}
      >
        <ExternalLinkIcon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
        {isMobileDropdown && t('header.open')}
      </button>

      {/* Audio toggle */}
      <button 
        onClick={toggleAudio}
        className={`flex rounded-full text-white hover:bg-gray-700 transition ${isMobileDropdown ? 'w-full justify-start px-3 py-2 text-left' : 'items-center justify-center w-8 h-8'}`}
        title={audioEnabled ? t('header.turnSoundOff') : t('header.turnSoundOn')}
      >
        {audioEnabled 
          ? <Volume2Icon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
          : <VolumeXIcon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
        }
        {isMobileDropdown && (audioEnabled ? t('header.soundOn') : t('header.soundOff'))}
      </button>

      {/* Theme toggle */}
      <button 
        onClick={toggleTheme}
        className={`flex rounded-full text-white hover:bg-gray-700 transition ${isMobileDropdown ? 'w-full justify-start px-3 py-2 text-left' : 'items-center justify-center w-8 h-8'}`}
        title={isDarkMode ? t('header.switchToLightMode') : t('header.switchToDarkMode')}
      >
        {isDarkMode
          ? <SunIcon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
          : <MoonIcon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
        }
        {isMobileDropdown && (isDarkMode ? t('header.lightMode') : t('header.darkMode'))}
      </button>

      {/* Fullscreen toggle */}
      <button 
        onClick={toggleFullScreen}
        className={`flex rounded-full text-white hover:bg-gray-700 transition ${isMobileDropdown ? 'w-full justify-start px-3 py-2 text-left' : 'items-center justify-center w-8 h-8'}`}
        title={t('header.toggleFullscreen')}
      >
        {isFullscreen
          ? <Minimize2Icon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
          : <Maximize2Icon className={`w-5 h-5${isMobileDropdown ? ' mr-2' : ''}`} />
        }
        {isMobileDropdown && t('header.fullscreen')}
      </button>

      {/* Adult content toggle */}
      <button 
        onClick={toggleAdultSpinners}
        className={`w-full flex items-center rounded-full p-0 text-white hover:bg-gray-700 ${isMobileDropdown ? 'justify-start px-3 py-2 text-left' : 'justify-center w-10 h-9'}`}
        title={showAdultSpinners ? t('header.hideAdultSpinners') : t('header.showAdultSpinners')}
      >
        {showAdultSpinners ? <EyeOffIcon className={`w-4.5 h-5 mr-2`} /> : <EyeIcon className={`w-4.5 h-5 mr-2`} />}
        {isMobileDropdown && (showAdultSpinners ? t('header.hideAdult') : t('header.showAdult'))}
      </button>
      
      {/* Language selector - now opens modal */}
      <div
        className={`w-full flex items-center gap-1 rounded-full px-3 py-2 text-white hover:bg-gray-700 cursor-pointer ${isMobileDropdown ? 'justify-start text-left' : 'justify-center h-10'} group relative`}
        onClick={() => {
          if (isMobileDropdown) {
            setShowMobileNavOptions(false);
            setTimeout(() => setShowLanguageModal(true), 100);
          } else {
            setShowLanguageModal(true);
          }
        }}
      >
        <GlobeIcon className={`w-5 h-5 mr-2`} />
        <span className="font-['Inter',Helvetica] font-normal text-gray-300 text-base">
          {getCurrentLanguageName()}
        </span>
      </div>

      {/* User button */}
      <button
        className={`w-full flex items-center rounded-full p-0 text-white hover:bg-gray-700 ${isMobileDropdown ? 'justify-start px-3 py-2 text-left mt-auto pt-3 border-t border-gray-700' : 'justify-center w-[36px] h-9'}`}
        onClick={() => handleUserClick(isMobileDropdown)}
      >
        <UserIcon className={`w-4.5 h-5 mr-2`} /> {isMobileDropdown && t('Profile')}
      </button>
    </div>
  );

  return (
    <div className="fixed top-0 left-0 right-0 h-[54px] bg-[#1e2434] shadow-[0px_4px_6px_#0000001a,0px_2px_4px_#0000001a] z-[60] fixed-header">
      <div className="flex justify-between h-full items-center px-2" style={{ paddingLeft: isMobile ? undefined : HEADER_LEFT_MARGIN }}>
        {/* Hamburger menu icon, shown only on mobile */}
        {isMobile && (
          <div className="relative">
            <button
              onClick={() => {
                onMobileMenuClick?.();
                dismissGuidance();
              }}
              className={`w-10 h-10 rounded-full text-white flex items-center justify-center ml-1 ${styles.burgerMenuButton} ${showGuidance ? styles.guidanceActive : ''}`}
              style={{ background: 'transparent', border: 'none' }}
              aria-label="Open menu"
            >
              <MenuIcon size={28} />
            </button>
          </div>
        )}
        {/* Application Title with Logo - Centered between hamburger and settings (Mobile only) */}
        {isMobile && (
          <div className="flex-1 flex items-center justify-center" style={{ height: '100%' }}>
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Spin Logo" 
                style={{ 
                  width: 32, 
                  height: 32, 
                  objectFit: 'contain', 
                  marginRight: 6,
                  display: 'block',
                  flexShrink: 0
                }} 
                onError={(e) => {
                  console.error('Logo failed to load:', e);
                }}
                onLoad={() => {
                  console.log('Logo loaded successfully');
                }}
              />
              <span className="text-white font-bold text-lg whitespace-nowrap" style={{ lineHeight: '54px', display: 'inline-block', flexShrink: 0 }}>{t('header.title')}</span>
            </div>
          </div>
        )}
        {/* Empty space for desktop to maintain layout */}
        {!isMobile && (
          <div className="flex-1" style={{ height: '100%' }}></div>
        )}
        {/* Settings cog icon, shown only on mobile to trigger the dropdown */}
        {isMobile && (
          <button
            onClick={toggleMobileNav}
            className="w-10 h-10 rounded-full text-white flex items-center justify-center mr-1"
            style={{ background: 'transparent', border: 'none' }}
            aria-label="Settings"
          >
            <SettingsIcon size={24} />
          </button>
        )}
        {/* Navigation icons for desktop view */}
        {!isMobile && (
          <div className="flex items-center px-5">
            {/* Nav icon buttons group */}
            <div className="flex items-center gap-2">
              {/* Share button */}
              <button onClick={() => { onShareClick(); }} className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" title={t('header.share')}>
                <Share2Icon className="w-5 h-5" />
              </button>
              {/* Open button */}
              <button onClick={() => { setShowSavedSpinners(true); }} className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" title={t('header.open')}>
                <ExternalLinkIcon className="w-5 h-5" />
              </button>
              {/* Audio toggle */}
              <button onClick={toggleAudio} className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" title={audioEnabled ? t('header.turnSoundOff') : t('header.turnSoundOn')}>
                {audioEnabled ? <Volume2Icon className="w-5 h-5" /> : <VolumeXIcon className="w-5 h-5" />}
              </button>
              {/* Theme toggle */}
              <button onClick={toggleTheme} className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" title={isDarkMode ? t('header.switchToLightMode') : t('header.switchToDarkMode')}>
                {isDarkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
              </button>
              {/* Fullscreen toggle */}
              <button onClick={toggleFullScreen} className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" title={t('header.toggleFullscreen')}>
                {isFullscreen ? <Minimize2Icon className="w-5 h-5" /> : <Maximize2Icon className="w-5 h-5" />}
              </button>
              {/* Adult content toggle */}
              <button onClick={toggleAdultSpinners} className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" title={showAdultSpinners ? t('header.hideAdultSpinners') : t('header.showAdultSpinners')}>
                {showAdultSpinners ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
            {/* Language selector and user/profile group */}
            <div className="flex items-center gap-2 ml-4">
              <div className="flex items-center gap-1 rounded-full px-3 py-2 text-white hover:bg-gray-700 cursor-pointer h-10 group relative"
                onClick={() => setShowLanguageModal(true)}>
                <GlobeIcon className="w-5 h-5 mr-2" />
                <span className="font-['Inter',Helvetica] font-normal text-gray-300 text-base">{getCurrentLanguageName()}</span>
              </div>
              <button className="flex items-center justify-center rounded-full w-8 h-8 text-white hover:bg-gray-700 transition" onClick={() => handleUserClick(false)}>
                <UserIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Dropdown menu for mobile navigation options */}
      {isMobile && showMobileNavOptions && (
        <div className="absolute top-[54px] right-0 bg-[#1e2434] shadow-lg rounded-b-lg overflow-hidden z-[70]"
          style={{ width: 'max-content', minWidth: 180, paddingLeft: 4, paddingRight: 4 }}>
          <div className="py-1 flex flex-col items-stretch">
            {renderNavIcons(true)}
          </div>
        </div>
      )}
      {/* Click-away listener to close the mobile dropdown when clicking outside */}
      {isMobile && showMobileNavOptions && (
        <div onClick={toggleMobileNav} className="fixed inset-0 z-[65]" />
      )}
      <LoginModal isOpen={showLoginModal} onRequestClose={() => setShowLoginModal(false)} />
      <ProfileModal isOpen={showProfileModal} onRequestClose={() => setShowProfileModal(false)} />
      <SavedSpinnersModal
        isOpen={showSavedSpinners}
        onClose={() => setShowSavedSpinners(false)}
        onShare={onSavedSpinnerShare}
        onOpen={onSavedSpinnerOpen}
        onLoginRequest={handleLoginRequest}
      />
    </div>
  );
};
